/**
 * Flashcard Page - Trang chính cho tính năng tạo và học Flashcards từ PDF
 */

import React, { useState, useEffect } from 'react'
import { ArrowLeft, FileText, Brain, BookOpen, Plus, Trash2, Eye, Edit } from 'lucide-react'
import { Link } from 'react-router-dom'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import PDFUploadInterface from '../../components/flashcard/PDFUploadInterface'
import FlashcardPreviewEditor from '../../components/flashcard/FlashcardPreviewEditor'
import FlashcardViewer from '../../components/flashcard/FlashcardViewer'
import { pdfProcessingService } from '../../services/pdf/PDFProcessingService'
import { pdfVisionService } from '../../services/pdf/PDFVisionService'
import { aiFlashcardService } from '../../services/flashcard/AIFlashcardService'
import { flashcardService } from '../../services/flashcard/FlashcardService'
import type { PDFProcessingResult } from '../../services/pdf/PDFProcessingService'
import type { FlashcardDraft, Flashcard, PDFDocument } from '../../types/flashcard'
import toast from 'react-hot-toast'

type Step = 'upload' | 'count_selector' | 'generating' | 'preview' | 'saved' | 'study' | 'edit'

const FlashcardPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>('upload')
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pdfResult, setPdfResult] = useState<PDFProcessingResult | null>(null)
  const [flashcardDrafts, setFlashcardDrafts] = useState<FlashcardDraft[]>([])
  const [savedFlashcards, setSavedFlashcards] = useState<Flashcard[]>([])
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [myDocuments, setMyDocuments] = useState<PDFDocument[]>([])
  const [selectedDocForStudy, setSelectedDocForStudy] = useState<PDFDocument | null>(null)
  const [flashcardCount, setFlashcardCount] = useState(15) // Số lượng flashcards muốn tạo
  const [showCountSelector, setShowCountSelector] = useState(false) // Hiển thị bước chọn số lượng
  const [stats, setStats] = useState({ total: 0, totalDocuments: 0, dueForReview: 0 })
  const [editingDocumentId, setEditingDocumentId] = useState<string>('')
  const [editingFlashcards, setEditingFlashcards] = useState<Flashcard[]>([])

  // Load saved documents on mount
  useEffect(() => {
    loadMyDocuments()
    loadStats()
  }, [])

  const loadMyDocuments = async () => {
    const docs = await flashcardService.getAllPDFDocuments()
    setMyDocuments(docs)
  }

  const loadStats = async () => {
    const userEmail = flashcardService['getUserEmail']?.() || 'default_user'
    const statsData = await flashcardService.getStatistics(userEmail)
    setStats(statsData)
  }

  // Handle PDF processed
  const handlePDFProcessed = async (result: PDFProcessingResult & { file: File }) => {
    setPdfFile(result.file)
    setPdfResult(result)

    // Show count selector instead of auto-generating
    setCurrentStep('count_selector')
    toast.success('PDF đã sẵn sàng! Chọn số lượng flashcards muốn tạo.')
  }

  // User confirms flashcard count and starts generation
  const startFlashcardGeneration = async () => {
    if (!pdfResult) {
      toast.error('Thiếu thông tin PDF')
      return
    }
    
    await generateFlashcards(pdfResult)
  }

  // Cancel count selection
  const cancelCountSelection = () => {
    setCurrentStep('upload')
    setPdfFile(null)
    setPdfResult(null)
  }

  // Generate flashcards from PDF
  const generateFlashcards = async (result: PDFProcessingResult) => {
    setIsGenerating(true)
    setCurrentStep('generating')
    toast.loading('Đang tạo flashcards từ AI...', { id: 'generating' })

    try {
      // Extract keywords
      const keywords = pdfProcessingService.extractKeywords(result.text || '', 15)

      // Generate flashcards with AI (use user-selected count)
      const response = await aiFlashcardService.generateFlashcards({
        documentId: 'temp',
        documentName: pdfFile?.name || 'Untitled Document',
        content: result.text || '',
        keywords,
        count: flashcardCount
      })

      if (!response.success || !response.drafts) {
        throw new Error(response.error || 'Không thể tạo flashcards')
      }

      setFlashcardDrafts(response.drafts)
      setCurrentStep('preview')
      
      toast.success('Đã tạo ' + response.drafts.length + ' flashcards!', { id: 'generating' })
      
      console.log('✅ Generated flashcards:', response.drafts.length)
      console.log('🪙 Tokens used:', response.tokensUsed)

    } catch (error) {
      console.error('Error generating flashcards:', error)
      toast.error('Có lỗi khi tạo flashcards. Vui lòng thử lại.', { id: 'generating' })
      setCurrentStep('upload')
    } finally {
      setIsGenerating(false)
    }
  }

  // Update flashcard drafts
  const handleUpdateDrafts = (updatedDrafts: FlashcardDraft[]) => {
    setFlashcardDrafts(updatedDrafts)
  }

  // Confirm and save flashcards
  const handleConfirmFlashcards = async () => {
    if (!pdfFile || !pdfResult) {
      toast.error('Thiếu thông tin tài liệu')
      return
    }

    toast.loading('Đang lưu flashcards...', { id: 'saving' })

    try {
      // Save PDF document
      const keywords = pdfProcessingService.extractKeywords(pdfResult.text || '', 15)
      const pdfDocument = await flashcardService.savePDFDocument({
        userId: 'default_user', // TODO: Get from auth context
        name: pdfFile.name.replace(/\.[^/.]+$/, ''), // Remove file extension for name
        filename: pdfFile.name,
        size: pdfFile.size,
        pageCount: pdfResult.pageCount || 0,
        uploadedAt: new Date(),
        flashcardCount: flashcardDrafts.length
      })

      // Save flashcards
      const savedCards = await flashcardService.saveFlashcards(
        flashcardDrafts,
        pdfDocument.id,
        pdfFile.name,
        'default_user'
      )

      setSavedFlashcards(savedCards)
      setCurrentDocumentId(pdfDocument.id)
      setCurrentStep('saved')
      loadMyDocuments()

      toast.success(`Đã lưu ${savedCards.length} flashcards!`, { id: 'saving' })

    } catch (error) {
      console.error('Error saving flashcards:', error)
      toast.error('Có lỗi khi lưu flashcards', { id: 'saving' })
    }
  }

  // Cancel and restart
  const handleCancel = () => {
    if (window.confirm('Bạn có chắc muốn hủy? Flashcards chưa lưu sẽ bị mất.')) {
      resetToUpload()
    }
  }

  // Reset to upload step
  const resetToUpload = () => {
    setPdfFile(null)
    setPdfResult(null)
    setFlashcardDrafts([])
    setSavedFlashcards([])
    setCurrentDocumentId('')
    setCurrentStep('upload')
  }

  // Start studying saved flashcards
  const startStudy = async (documentId: string) => {
    const cards = await flashcardService.getFlashcardsByDocument(documentId)
    if (cards.length === 0) {
      toast.error('Không tìm thấy flashcards cho tài liệu này')
      return
    }

    setSavedFlashcards(cards)
    const doc = await flashcardService.getPDFDocument(documentId)
    setSelectedDocForStudy(doc)
    setCurrentStep('study')
  }

  // Handle review callback
  const handleReview = async (flashcardId: string, correct: boolean) => {
    await flashcardService.recordReview(flashcardId, correct ? 5 : 2, correct)
    toast.success(correct ? '✅ Tuyệt vời!' : '💪 Tiếp tục cố gắng!')
  }

  // Edit document flashcards
  const editDocument = async (documentId: string) => {
    const cards = await flashcardService.getFlashcardsByDocument(documentId)
    if (cards.length === 0) {
      toast.error('Không tìm thấy flashcards cho tài liệu này')
      return
    }

    setEditingDocumentId(documentId)
    setEditingFlashcards(cards)
    setCurrentStep('edit')
  }

  // Save edited flashcards
  const handleSaveEdited = async (updatedCards: Flashcard[]) => {
    try {
      toast.loading('Đang lưu thay đổi...', { id: 'saving-edit' })

      // Update từng flashcard
      for (const card of updatedCards) {
        await flashcardService.updateFlashcard(card.id, {
          front: card.front,
          back: card.back,
          difficulty: card.difficulty,
          tags: card.tags
        })
      }

      // Auto-export sau khi edit
      const allFlashcards = await flashcardService.getAllFlashcards()
      const allDocuments = await flashcardService.getAllPDFDocuments()
      
      // Import auto-export service
      const { autoExportService } = await import('../../services/flashcard/AutoExportService')
      await autoExportService.autoExport(allFlashcards, allDocuments)

      toast.success('Đã lưu thay đổi!', { id: 'saving-edit' })
      setCurrentStep('upload')
      setEditingDocumentId('')
      setEditingFlashcards([])
      loadMyDocuments()
      loadStats()
    } catch (error) {
      console.error('Error saving edits:', error)
      toast.error('Có lỗi khi lưu thay đổi', { id: 'saving-edit' })
    }
  }

  // Cancel editing
  const handleCancelEdit = () => {
    if (window.confirm('Hủy chỉnh sửa? Các thay đổi chưa lưu sẽ bị mất.')) {
      setCurrentStep('upload')
      setEditingDocumentId('')
      setEditingFlashcards([])
    }
  }

  // Delete document
  const deleteDocument = async (documentId: string) => {
    if (window.confirm('Bạn có chắc muốn xóa tài liệu và tất cả flashcards liên quan?')) {
      const success = await flashcardService.deletePDFDocument(documentId)
      if (success) {
        toast.success('Đã xóa tài liệu')
        loadMyDocuments()
      } else {
        toast.error('Có lỗi khi xóa tài liệu')
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Quay lại trang chủ
          </Link>

          <div className="text-center mb-6">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg font-bold rounded-full mb-4 shadow-lg">
              <Brain className="w-6 h-6 mr-3" />
              Flashcards từ PDF
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-700 mb-2">
              Tạo Flashcards thông minh với AI
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Upload tài liệu PDF, AI sẽ tự động tạo flashcards giúp bạn học hiệu quả hơn
            </p>
          </div>

          {/* Statistics */}
          {stats.total > 0 && currentStep === 'upload' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
                <p className="text-sm text-gray-600">Flashcards</p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-purple-600">{stats.totalDocuments}</p>
                <p className="text-sm text-gray-600">Tài liệu</p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-green-600">{stats.dueForReview}</p>
                <p className="text-sm text-gray-600">Cần ôn</p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-orange-600">
                  {stats.total > 0 ? Math.round((stats.dueForReview / stats.total) * 100) : 0}%
                </p>
                <p className="text-sm text-gray-600">Tiến độ</p>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Step: Upload */}
          {currentStep === 'upload' && (
            <div>
              <PDFUploadInterface onPDFProcessed={handlePDFProcessed} />

              {/* My Documents */}
              {myDocuments.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Tài liệu của tôi ({myDocuments.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myDocuments.map(doc => (
                      <div key={doc.id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-800 truncate">{doc.filename}</h4>
                            <p className="text-sm text-gray-600">
                              {doc.flashcardCount} flashcards • {new Date(doc.uploadedAt).toLocaleDateString('vi-VN')}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2 mt-3">
                          <button
                            onClick={() => startStudy(doc.id)}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm font-semibold"
                          >
                            <Eye className="w-4 h-4" />
                            <span>Học</span>
                          </button>
                          <button
                            onClick={() => editDocument(doc.id)}
                            className="px-4 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 flex items-center justify-center"
                            title="Chỉnh sửa flashcards"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteDocument(doc.id)}
                            className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 flex items-center justify-center"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Count Selector - Chọn số lượng Flashcards */}
          {currentStep === 'count_selector' && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <div className="inline-block p-6 bg-green-100 rounded-full mb-4">
                  <FileText className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  PDF đã sẵn sàng!
                </h2>
                <p className="text-gray-600">
                  Chọn số lượng flashcards bạn muốn tạo từ tài liệu này
                </p>
              </div>

              {/* PDF Info */}
              {pdfFile && pdfResult && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-800">{pdfFile.name}</h3>
                      <p className="text-sm text-gray-600">
                        📄 {pdfResult.pageCount} trang • 📝 {pdfResult.text?.length.toLocaleString()} ký tự
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Flashcard Count Selector */}
              <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
                <label className="block text-lg font-bold text-purple-900 mb-4 text-center">
                  🎯 Số lượng Flashcards: <span className="text-3xl text-purple-600">{flashcardCount}</span>
                </label>
                
                {/* Slider */}
                <div className="mb-4">
                  <input
                    type="range"
                    min="5"
                    max="30"
                    step="5"
                    value={flashcardCount}
                    onChange={(e) => setFlashcardCount(parseInt(e.target.value))}
                    className="w-full h-3 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                  />
                  <div className="flex justify-between text-xs text-purple-600 mt-2 font-medium">
                    <span>5</span>
                    <span>10</span>
                    <span>15</span>
                    <span>20</span>
                    <span>25</span>
                    <span>30</span>
                  </div>
                </div>

                {/* Quick Select Buttons */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[10, 15, 20].map(count => (
                    <button
                      key={count}
                      onClick={() => setFlashcardCount(count)}
                      className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                        flashcardCount === count
                          ? 'bg-purple-600 text-white shadow-lg scale-105'
                          : 'bg-white text-purple-600 border-2 border-purple-300 hover:border-purple-500'
                      }`}
                    >
                      {count} cards
                    </button>
                  ))}
                </div>

                {/* Info */}
                <div className="p-3 bg-white rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-700 mb-2">
                    <strong>Gợi ý:</strong>
                  </p>
                  <ul className="text-xs text-purple-600 space-y-1">
                    <li>• <strong>5-10 cards:</strong> Tài liệu ngắn, tập trung vào khái niệm chính</li>
                    <li>• <strong>15 cards:</strong> Khuyến nghị cho tài liệu trung bình (default) ⭐</li>
                    <li>• <strong>20-30 cards:</strong> Tài liệu dài, coverage toàn diện</li>
                    <li>• <strong>Lưu ý:</strong> Cards nhiều hơn = chi phí AI cao hơn</li>
                  </ul>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  onClick={cancelCountSelection}
                  className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 font-semibold transition-colors"
                >
                  ← Quay lại
                </button>

                <button
                  onClick={startFlashcardGeneration}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 font-semibold transition-all flex items-center space-x-2 shadow-lg"
                >
                  <Brain className="w-5 h-5" />
                  <span>Tạo {flashcardCount} Flashcards với AI</span>
                </button>
              </div>
            </div>
          )}

          {/* Step: Generating */}
          {currentStep === 'generating' && (
            <div className="text-center py-12">
              <div className="inline-block p-6 bg-purple-100 rounded-full mb-4">
                <Brain className="w-12 h-12 text-purple-600 animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                AI đang tạo Flashcards...
              </h2>
              <p className="text-gray-600">
                Vui lòng đợi trong giây lát. AI đang phân tích tài liệu và tạo flashcards chất lượng cao.
              </p>
            </div>
          )}

          {/* Step: Preview & Edit */}
          {currentStep === 'preview' && (
            <FlashcardPreviewEditor
              drafts={flashcardDrafts}
              onUpdate={handleUpdateDrafts}
              onConfirm={handleConfirmFlashcards}
              onCancel={handleCancel}
            />
          )}

          {/* Step: Saved */}
          {currentStep === 'saved' && (
            <div className="text-center py-12">
              <div className="inline-block p-6 bg-green-100 rounded-full mb-4">
                <BookOpen className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Đã lưu {savedFlashcards.length} Flashcards!
              </h2>
              <p className="text-gray-600 mb-6">
                Flashcards của bạn đã được lưu thành công. Bạn có thể bắt đầu học ngay hoặc tạo thêm từ tài liệu khác.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => startStudy(currentDocumentId)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold flex items-center space-x-2"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Bắt đầu học</span>
                </button>
                <button
                  onClick={resetToUpload}
                  className="px-8 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Tạo thêm</span>
                </button>
              </div>
            </div>
          )}

          {/* Step: Study */}
          {currentStep === 'study' && (
            <div>
              <FlashcardViewer
                flashcards={savedFlashcards}
                onReview={handleReview}
                onClose={() => {
                  resetToUpload()
                  loadMyDocuments()
                }}
              />
            </div>
          )}

          {/* Step: Edit - Chỉnh sửa flashcards đã lưu */}
          {currentStep === 'edit' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                      <Edit className="w-6 h-6 mr-3 text-green-600" />
                      Chỉnh sửa Flashcards
                    </h2>
                    <p className="text-gray-600 mt-2">
                      Chỉnh sửa flashcards đã lưu. Sau khi lưu, file sẽ tự động export để bạn build lại.
                    </p>
                  </div>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                </div>

                {/* Flashcard Editor */}
                <FlashcardPreviewEditor
                  drafts={editingFlashcards.map(card => ({
                    id: card.id,
                    userId: card.authorId,
                    documentId: card.documentId || '',
                    documentName: card.documentName || '',
                    front: card.front,
                    back: card.back,
                    difficulty: card.difficulty,
                    tags: card.tags || [],
                    isEdited: false,
                    source: '' // Empty source for existing flashcards
                  }))}
                  onUpdate={(updatedDrafts) => {
                    // Update editing flashcards
                    const updated = editingFlashcards.map((card, idx) => ({
                      ...card,
                      front: updatedDrafts[idx]?.front || card.front,
                      back: updatedDrafts[idx]?.back || card.back,
                      difficulty: updatedDrafts[idx]?.difficulty || card.difficulty,
                      tags: updatedDrafts[idx]?.tags || card.tags
                    }))
                    setEditingFlashcards(updated)
                  }}
                  onConfirm={() => handleSaveEdited(editingFlashcards)}
                  confirmButtonText="Lưu thay đổi"
                />

                <div className="mt-6 flex justify-between items-center">
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={() => handleSaveEdited(editingFlashcards)}
                    className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-bold shadow-lg"
                  >
                    💾 Lưu & Export
                  </button>
                </div>

                {/* Info box */}
                <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800 flex items-start">
                    <Brain className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Lưu ý:</strong> Sau khi lưu, file <code className="bg-blue-100 px-2 py-0.5 rounded">quants82@gmail.com.json</code> sẽ tự động download.
                      Chạy <code className="bg-blue-100 px-2 py-0.5 rounded">npm run auto-copy</code> và <code className="bg-blue-100 px-2 py-0.5 rounded">npm run build</code> để cập nhật lên host.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default FlashcardPage

